'use client';

/**
 * @file PreAuthPreviewSection
 * @description Preview table for pre-auth imported instruments
 * Shows instrument data, stats, and conversion CTAs
 */

import { useMemo } from 'react';
import Link from 'next/link';
import { AlertTriangle, Clock, CheckCircle2, Shield, Trash2, ArrowRight, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { PreAuthInstrument } from '@/lib/pre-auth/storage';
import { calculatePreAuthStats } from '@/lib/pre-auth/storage';

interface PreAuthPreviewSectionProps {
  instruments: PreAuthInstrument[];
  fileName: string;
  timeRemaining: string | null;
  onClear: () => void;
  onSignUp: () => void;
  onReviewMapping?: () => void;
}

function getStatusBadge(nextDue: string | null): {
  variant: 'destructive' | 'secondary' | 'default';
  label: string;
  icon: React.ReactNode;
} {
  if (!nextDue) {
    return {
      variant: 'secondary',
      label: 'No date',
      icon: null,
    };
  }

  const dueDate = new Date(nextDue);
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  if (dueDate < now) {
    return {
      variant: 'destructive',
      label: 'Overdue',
      icon: <AlertTriangle className="h-3 w-3" />,
    };
  }

  if (dueDate < thirtyDaysFromNow) {
    return {
      variant: 'secondary',
      label: 'Due soon',
      icon: <Clock className="h-3 w-3" />,
    };
  }

  return {
    variant: 'default',
    label: 'Valid',
    icon: <CheckCircle2 className="h-3 w-3" />,
  };
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

export function PreAuthPreviewSection({
  instruments,
  fileName,
  timeRemaining,
  onClear,
  onSignUp,
  onReviewMapping,
}: PreAuthPreviewSectionProps) {
  const stats = useMemo(() => calculatePreAuthStats(instruments), [instruments]);
  const displayInstruments = instruments.slice(0, 10);
  const hasMore = instruments.length > 10;

  return (
    <section className="w-full max-w-5xl mx-auto space-y-6 py-8">
      {/* Header with file info and time remaining */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            Your Instruments Preview
          </h2>
          <p className="text-muted-foreground mt-1">
            {instruments.length} instruments imported from <span className="font-medium">{fileName}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {onReviewMapping && (
            <Button variant="ghost" size="sm" onClick={onReviewMapping}>
              Review mapping
            </Button>
          )}
          {timeRemaining && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full">
              <Timer className="h-4 w-4" />
              <span>Data expires in {timeRemaining}</span>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={cn(stats.overdue > 0 && 'border-destructive/50 bg-destructive/5')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Overdue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={cn(
              'text-3xl font-bold',
              stats.overdue > 0 ? 'text-destructive' : 'text-muted-foreground'
            )}>
              {stats.overdue}
            </div>
            {stats.overdue > 0 && (
              <p className="text-xs text-destructive mt-1">Needs attention</p>
            )}
          </CardContent>
        </Card>

        <Card className={cn(stats.dueSoon > 0 && 'border-amber-500/50 bg-amber-500/5')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Due in 30 days
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={cn(
              'text-3xl font-bold',
              stats.dueSoon > 0 ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground'
            )}>
              {stats.dueSoon}
            </div>
            {stats.dueSoon > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">Schedule soon</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Valid
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600 dark:text-green-500">
              {stats.valid}
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary/50 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Audit Readiness
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={cn(
              'text-3xl font-bold',
              stats.readinessScore >= 80 ? 'text-green-600 dark:text-green-500' :
              stats.readinessScore >= 50 ? 'text-amber-600 dark:text-amber-500' :
              'text-destructive'
            )}>
              {stats.readinessScore}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.readinessScore >= 80 ? 'Good standing' :
               stats.readinessScore >= 50 ? 'Needs improvement' :
               'At risk'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Instruments Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Instrument Preview</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Next Due</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayInstruments.map((instrument) => {
                  const status = getStatusBadge(instrument.nextDue);
                  return (
                    <TableRow key={instrument.assetId}>
                      <TableCell className="font-mono text-sm">
                        {instrument.assetId}
                      </TableCell>
                      <TableCell className="font-medium">
                        {instrument.name}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {instrument.location || '—'}
                      </TableCell>
                      <TableCell>
                        {formatDate(instrument.nextDue)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={status.variant} className="gap-1">
                          {status.icon}
                          {status.label}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {hasMore && (
            <div className="px-4 py-3 border-t bg-muted/30 text-center text-sm text-muted-foreground">
              Showing 10 of {instruments.length} instruments. Sign up to see all.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversion CTA Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1 space-y-3">
              <h3 className="text-xl font-semibold">
                Save your instruments to your account
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Get email reminders before calibrations expire
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Generate audit-ready PDF reports in one click
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Track certificates and calibration history
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-green-600" />
                  Your data is encrypted and secure
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-3 w-full md:w-auto">
              <Button size="lg" onClick={onSignUp} className="gap-2">
                Save to Account
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Link href="/auth/login" className="text-center">
                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  Already have an account? Sign in
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clear data option */}
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="text-muted-foreground hover:text-destructive gap-2"
        >
          <Trash2 className="h-4 w-4" />
          Clear imported data and start over
        </Button>
      </div>
    </section>
  );
}

export default PreAuthPreviewSection;
